import { Dispatch, SetStateAction, memo } from 'react';
import { Anchor, Badge, Grid, Group } from '@mantine/core';
import { type MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import Table from '../../../components/Table';
import moment from 'moment';
import { AttendanceReportTableInfoData } from '.';
import { AttendanceLocationMetadata } from '../../../services/attendance.service';

const LOCATION_STATUS_LABELS: Record<string, string> = {
  granted: 'Capturada',
  denied: 'Denegada',
  unavailable: 'No disponible',
  not_requested: 'No solicitada',
  error: 'Error'
};

const LOCATION_STATUS_COLORS: Record<string, string> = {
  granted: 'teal',
  denied: 'red',
  unavailable: 'gray',
  not_requested: 'gray',
  error: 'orange'
};

export const buildAttendanceLocationMapUrl = (location?: AttendanceLocationMetadata | null) => {
  if (
    location?.permissionStatus !== 'granted' ||
    typeof location.latitude !== 'number' ||
    typeof location.longitude !== 'number'
  ) {
    return null;
  }

  return `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;
};

export const getAttendanceLocationLabel = (
  location?: AttendanceLocationMetadata | null,
  summary?: string
) => {
  if (!location) return summary || '';
  if (summary) return summary.replace('Ubicación capturada', 'Capturada');

  return LOCATION_STATUS_LABELS[location.permissionStatus || ''] || '';
};

const renderAttendanceLocation = (
  location?: AttendanceLocationMetadata | null,
  summary?: string
) => {
  const label = getAttendanceLocationLabel(location, summary);
  if (!label) return '-';

  const mapUrl = buildAttendanceLocationMapUrl(location);
  const color = LOCATION_STATUS_COLORS[location?.permissionStatus || ''] || 'gray';

  return (
    <Group gap={6} wrap="nowrap">
      <Badge color={color} variant="light" size="sm">
        {label}
      </Badge>
      {mapUrl ? (
        <Anchor href={mapUrl} target="_blank" rel="noreferrer" size="xs">
          Maps
        </Anchor>
      ) : null}
    </Group>
  );
};

export default memo(function TableView({
  tableInfoData = [],
  totalRowsCount = 0,
  pages = 0,
  isError = false,
  isLoading = false,
  pagination,
  setPagination
}: {
  tableInfoData: AttendanceReportTableInfoData[];
  totalRowsCount: any;
  pages: any;
  isError: boolean;
  isLoading: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
}) {
  const { t } = useTranslation(['FieldName', 'Attendance']);
  const columns = [
    {
      accessorFn: (row: AttendanceReportTableInfoData) =>
        row.employee?.name || row.employee?.email || 'N/A',
      header: t('FieldName:employee')
    },
    {
      accessorFn: (row: AttendanceReportTableInfoData) =>
        moment(row.clockInTime).format('DD/MM/YYYY'),
      header: t('FieldName:date')
    },
    {
      accessorFn: (row: AttendanceReportTableInfoData) => moment(row.clockInTime).format('HH:mm'),
      header: t('FieldName:clockInTime')
    },
    {
      accessorFn: (row: AttendanceReportTableInfoData) =>
        row.clockOutTime ? moment(row.clockOutTime).format('HH:mm') : 'En turno',
      header: t('FieldName:clockOutTime')
    },
    {
      accessorFn: (row: AttendanceReportTableInfoData) => {
        if (!row.shiftDuration) return 'En turno';
        const { hours, minutes } = row.shiftDuration;
        return `${hours}h ${minutes}m`;
      },
      header: t('FieldName:duration')
    },
    {
      accessorFn: (row: AttendanceReportTableInfoData) => row.clockInStore || 'N/A',
      header: t('FieldName:store')
    },
    {
      accessorFn: (row: AttendanceReportTableInfoData) => {
        if (row.autoClosed) {
          return (
            <Badge color="orange" variant="light">
              Auto-cerrado
            </Badge>
          );
        }
        if (!row.clockOutTime) {
          return (
            <Badge color="green" variant="light">
              Activo
            </Badge>
          );
        }
        return (
          <Badge color="blue" variant="light">
            Completado
          </Badge>
        );
      },
      header: t('FieldName:status')
    },
    {
      accessorFn: (row: AttendanceReportTableInfoData) => row.notes || '-',
      header: t('FieldName:notes')
    },
    {
      accessorFn: (row: AttendanceReportTableInfoData) => row.clockInIpAddress || 'N/A',
      header: t('FieldName:clockInIpAddress')
    },
    {
      accessorFn: (row: AttendanceReportTableInfoData) =>
        renderAttendanceLocation(row.clockInLocation, row.clockInLocationSummary),
      header: t('FieldName:clockInLocation')
    },
    {
      accessorFn: (row: AttendanceReportTableInfoData) => row.clockOutIpAddress || 'N/A',
      header: t('FieldName:clockOutIpAddress')
    },
    {
      accessorFn: (row: AttendanceReportTableInfoData) =>
        renderAttendanceLocation(row.clockOutLocation, row.clockOutLocationSummary),
      header: t('FieldName:clockOutLocation')
    }
  ];

  return (
    <>
      <Grid gutter="md">
        <Grid.Col span={12}>
          <Table
            tableColumns={columns}
            pagination={pagination}
            setPagination={setPagination}
            tableData={tableInfoData}
            isError={isError}
            paginationIsLoading={isLoading}
            numPages={pages}
            totalRowsCount={totalRowsCount}
            overrideTableConfigs={{
            }}
          />
        </Grid.Col>
      </Grid>
    </>
  );
});
